import { CalculationState } from '@/lib/calculationstate';
import params from '@/lib/calculationparams';
import { round } from 'lodash-es';
import {
  cappedSalary,
  correctionFactor,
  currentToRemainingIncomeRatio,
  lookupTable,
} from './helpers';

/**
 * Determines whether the current calculation state is eligible for a LGU
 * (Loon Gerelateerde Uitkering).
 *
 * @param state
 */
export function hasLGU(state: CalculationState): boolean {
  return state.disabilityRatio >= 0.35
    && (state.disabilityRatio < 0.8 || !state.cannotRecover);
}

/**
 * Calculates the amount awarded in a LGU (Loon Gerelateerde Uitkering)
 *
 * Returns 0 if the state is not eligible for a LGU.
 *
 * @param state
 */
export function calculateLGUAmount(state: CalculationState): number {
  if (!hasLGU(state)) return 0;

  const payout = params.uwv.lguPayoutPercent * (
    cappedSalary(state) - (state.extraIncome * correctionFactor(state))
  );

  // TODO: migrate to some non floating point number type
  return round(payout, 2);
}

/**
 * Determines whether the current calculation state is eligible for a VVU
 * (Vervolg Uitkering)
 *
 * @param state
 */
export function hasVVU(state: CalculationState): boolean {
  const checkRatio = state.disabilityRatio >= 0.35 && state.disabilityRatio < 0.8;
  const checkIncome = currentToRemainingIncomeRatio(state) < params.uwv.vvuCutoffPercent;

  return checkRatio && checkIncome;
}

/**
 * Calculates the amount awarded in a VVU (Vervolg Uitkering)
 *
 * Returns 0 if the state is not eligible for a VVU.
 *
 * @param state
 */
export function calculateVVUAmount(state: CalculationState): number {
  if (!hasVVU(state)) return 0;

  const payout = params.uwv.minimumSalary * (lookupTable(params.uwv.vvuPayoutPercentages, state.disabilityRatio) ?? 0);

  // TODO: migrate to some non floating point number type
  return round(payout, 2);
}

/**
 * Determines whether the current calculation state is eligible for a LAU
 * (Loon Aanvullings Uitkering)
 *
 * @param state
 */
export function hasLAU(state: CalculationState): boolean {
  const normalDisabilityCheck = state.disabilityRatio >= 0.35 && state.disabilityRatio < 0.8
    && currentToRemainingIncomeRatio(state) >= params.uwv.lauCutoffPercent;
  const highDisabilityCheck = state.disabilityRatio >= 0.8 && !state.cannotRecover;

  return normalDisabilityCheck || highDisabilityCheck;
}

/**
 * Calculates the amount awarded in a LAU (Loon Aanvullings Uitkering)
 * Returns 0 if the state is not eligible for a LAU.
 *
 * @param state
 */
export function calculateLAUAmount(state: CalculationState): number {
  if (!hasLAU(state)) {
    return 0;
  }

  const payout = params.uwv.lauPayoutPercent * (cappedSalary(state) - (state.extraIncome * correctionFactor(state)));
  return round(payout, 2);
}

/**
 * Determines whether the current calculation state is eligible for an IVA-subsidy
 * (regeling Inkomensvoorziening Volledig Arbeidsongeschikten)
 *
 * @param state
 */
export function hasIVA(state: CalculationState): boolean {
  return state.disabilityRatio >= 0.8 && state.cannotRecover;
}

/**
 * Calculates the amount awarded in an IVA-subsidy
 * (regeling Inkomensvoorziening Volledig Arbeidsongeschikten)
 *
 * Returns 0 if the state is not eligible for an IVA.
 *
 * @param state
 */
export function calculateIVAAmount(state: CalculationState): number {
  if (!hasIVA(state)) {
    return 0;
  }

  const payout = params.uwv.ivaPayoutPercent * (cappedSalary(state) - (state.extraIncome * correctionFactor(state)));

  return round(payout, 2);
}
